import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { ResponsiveCalendar } from '@nivo/calendar'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { addYears, endOfYear, startOfYear, subYears, format } from 'date-fns'

import { grey } from '@mui/material/colors'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import ChartLegend from 'components/billing/groups/common/ChartLegend'

import { get } from 'utils/lodash'
import { COMMON } from 'theme/colors'
import { FRONTEND_DATE_WITH_WEEKDAY_FORMAT_DATE_FNS } from 'settings/constants/date'
import { formatHeatmapData, colorScaleFn, HEATMAP_VALUE_TO_BILLING, CHART_LEGEND } from './utils'

const MARGIN = { top: 20, right: 36, bottom: 20, left: 56 }

const UnbilledDaysChart = ({ data = {} }) => {
  const [startDate, setStartDate] = useState(startOfYear(new Date()))
  const [endDate, setEndDate] = useState(endOfYear(new Date()))
  const graphData = useMemo(() => formatHeatmapData(data), [data])

  const heatmapSx = { height: 150 }

  const handleNextDateClick = () => {
    const newStartDate = addYears(endDate, 1)
    setStartDate(startOfYear(newStartDate))
    setEndDate(endOfYear(newStartDate))
  }

  const handlePreviousDateClick = () => {
    const newStartDate = subYears(endDate, 1)
    setStartDate(startOfYear(newStartDate))
    setEndDate(endOfYear(newStartDate))
  }

  const renderTooltip = row => {
    const { date, color, value } = row
    const dateFEFormat = format(date, FRONTEND_DATE_WITH_WEEKDAY_FORMAT_DATE_FNS)
    const type = get(HEATMAP_VALUE_TO_BILLING, `${value}.type`)

    return (
      <Box px={0.5} bgcolor={COMMON.white} display="flex" justifyContent="center" alignItems="center" gap={0.5}>
        <Box bgcolor={color} height="12px" width="12px" />
        <Typography fontWeight={700}>{`${dateFEFormat}: ${type}`}</Typography>
      </Box>
    )
  }

  return (
    <>
      <Box position="relative" width="100%" sx={heatmapSx}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          height="100%"
          position="absolute"
          px={0.5}
        >
          <IconButton sx={{ position: 'relative', zIndex: 1 }} onClick={handlePreviousDateClick}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton sx={{ position: 'relative', zIndex: 1 }} onClick={handleNextDateClick}>
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
        <Box
          width="calc(100% - 64px)"
          margin="0 auto"
          position="relative"
          overflow="hidden"
          zIndex={2}
          sx={{
            ...heatmapSx,
            '& svg text': {
              fontWeight: 700,
            },
            '& > div > div': {
              position: 'fixed!important',
            },
          }}
        >
          <ResponsiveCalendar
            theme={{ fontSize: 14, legends: { text: { fontSize: 14 } } }}
            data={graphData}
            from={startDate}
            to={endDate}
            tooltip={renderTooltip}
            colorScale={colorScaleFn}
            emptyColor={grey[100]}
            monthLegendOffset={4}
            margin={MARGIN}
            monthBorderColor="#ffffff"
            dayBorderWidth={2}
            dayBorderColor="#ffffff"
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'row',
                translateY: 36,
                itemCount: 4,
                itemWidth: 42,
                itemHeight: 36,
                itemsSpacing: 14,
                itemDirection: 'right-to-left',
              },
            ]}
          />
        </Box>
      </Box>
      <ChartLegend showZeroValueLegend data={CHART_LEGEND} />
    </>
  )
}

UnbilledDaysChart.propTypes = {
  data: PropTypes.array,
}

export default UnbilledDaysChart
